import { useState, useEffect } from 'react';
import { apiGet, apiPut } from './api-client';

// ============================================
// Types
// ============================================

export type VisualStyle = 'glassmorphic' | 'neumorphic' | 'neubrutalism';

export interface ThemeConfig {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  surfaceColor: string;
  textColor: string;
  fontFamily: string;
  visualStyle: VisualStyle;
  customCSS?: string;
  // Gradient settings
  gradientEnabled: boolean;
  gradientStartColor: string | null; // null = use primaryColor
  gradientMiddleColor: string | null; // null = use secondaryColor
  gradientEndColor: string | null; // null = use accentColor
  buttonGlowEnabled: boolean;
}

const DEFAULT_THEME: ThemeConfig = {
  primaryColor: '#f5b041', // Gold/Amber
  secondaryColor: '#e74c3c', // Red
  accentColor: '#5dade2', // Blue
  backgroundColor: '#1c1c1e', // Dark background
  surfaceColor: '#2c2c2e', // Card/panel background
  textColor: '#f5f5f7', // Light text
  fontFamily: 'Inter',
  visualStyle: 'glassmorphic',
  customCSS: '',
  gradientEnabled: true,
  gradientStartColor: null, // Use primaryColor
  gradientMiddleColor: null, // Use secondaryColor
  gradientEndColor: null, // Use accentColor
  buttonGlowEnabled: true,
};

// ============================================
// Theme Manager Singleton
// ============================================

export class ThemeManager {
  private static instance: ThemeManager;
  private currentTheme: ThemeConfig = DEFAULT_THEME;
  private customStyleElement: HTMLStyleElement | null = null;

  private constructor() {
    // Private constructor for singleton pattern
  }

  static getInstance(): ThemeManager {
    if (!ThemeManager.instance) {
      ThemeManager.instance = new ThemeManager();
    }
    return ThemeManager.instance;
  }

  /**
   * Load theme from API and apply to DOM
   */
  async loadTheme(): Promise<ThemeConfig> {
    try {
      const data = await apiGet<Record<string, any>>(
        '/api/public/settings?keys=theme.primary_color,theme.secondary_color,theme.accent_color,theme.background_color,theme.surface_color,theme.text_color,theme.font_family,theme.custom_css,theme.visual_style,theme.gradient_enabled,theme.gradient_start,theme.gradient_middle,theme.gradient_end,theme.button_glow_enabled'
      );

      const theme: ThemeConfig = { ...DEFAULT_THEME };

      if (data['theme.primary_color']) {
        theme.primaryColor = data['theme.primary_color']?.color || DEFAULT_THEME.primaryColor;
      }
      if (data['theme.secondary_color']) {
        theme.secondaryColor = data['theme.secondary_color']?.color || DEFAULT_THEME.secondaryColor;
      }
      if (data['theme.accent_color']) {
        theme.accentColor = data['theme.accent_color']?.color || DEFAULT_THEME.accentColor;
      }
      if (data['theme.background_color']) {
        theme.backgroundColor = data['theme.background_color']?.color || DEFAULT_THEME.backgroundColor;
      }
      if (data['theme.surface_color']) {
        theme.surfaceColor = data['theme.surface_color']?.color || DEFAULT_THEME.surfaceColor;
      }
      if (data['theme.text_color']) {
        theme.textColor = data['theme.text_color']?.color || DEFAULT_THEME.textColor;
      }
      if (data['theme.font_family']) {
        theme.fontFamily = data['theme.font_family']?.font || DEFAULT_THEME.fontFamily;
      }
      if (data['theme.custom_css']) {
        theme.customCSS = data['theme.custom_css']?.css || '';
      }
      if (data['theme.visual_style']) {
        theme.visualStyle = (data['theme.visual_style']?.style as VisualStyle) || DEFAULT_THEME.visualStyle;
      }
      if (data['theme.gradient_enabled'] !== undefined) {
        theme.gradientEnabled = data['theme.gradient_enabled']?.enabled ?? DEFAULT_THEME.gradientEnabled;
      }
      if (data['theme.gradient_start'] !== undefined) {
        theme.gradientStartColor = data['theme.gradient_start']?.color || null;
      }
      if (data['theme.gradient_middle'] !== undefined) {
        theme.gradientMiddleColor = data['theme.gradient_middle']?.color || null;
      }
      if (data['theme.gradient_end'] !== undefined) {
        theme.gradientEndColor = data['theme.gradient_end']?.color || null;
      }
      if (data['theme.button_glow_enabled'] !== undefined) {
        theme.buttonGlowEnabled = data['theme.button_glow_enabled']?.enabled ?? DEFAULT_THEME.buttonGlowEnabled;
      }

      this.currentTheme = theme;
      this.applyTheme(theme);

      return theme;
    } catch (error) {
      console.error('Error loading theme:', error);
      this.applyTheme(DEFAULT_THEME);
      return DEFAULT_THEME;
    }
  }

  /**
   * Convert hex color to RGB space-separated values
   */
  private hexToRgb(hex: string): string {
    const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!match) return '0 0 0';

    const r = parseInt(match[1]!, 16);
    const g = parseInt(match[2]!, 16);
    const b = parseInt(match[3]!, 16);
    return `${r} ${g} ${b}`;
  }

  /**
   * Apply theme to DOM
   */
  private applyTheme(theme: ThemeConfig): void {
    const root = document.documentElement;

    // Set CSS variables
    root.style.setProperty('--color-primary', theme.primaryColor);
    root.style.setProperty('--color-secondary', theme.secondaryColor);
    root.style.setProperty('--color-accent', theme.accentColor);
    root.style.setProperty('--color-bg', theme.backgroundColor);
    root.style.setProperty('--color-surface', theme.surfaceColor);
    root.style.setProperty('--color-text', theme.textColor);
    root.style.setProperty('--font-family', theme.fontFamily);

    // Set RGB versions for Tailwind opacity modifiers
    root.style.setProperty('--color-bg-rgb', this.hexToRgb(theme.backgroundColor));
    root.style.setProperty('--color-surface-rgb', this.hexToRgb(theme.surfaceColor));

    // Set gradient CSS variables (fallback to brand colors if null)
    const gradientStart = theme.gradientStartColor || theme.primaryColor;
    const gradientMiddle = theme.gradientMiddleColor || theme.secondaryColor;
    const gradientEnd = theme.gradientEndColor || theme.accentColor;

    root.style.setProperty('--gradient-start', gradientStart);
    root.style.setProperty('--gradient-middle', gradientMiddle);
    root.style.setProperty('--gradient-end', gradientEnd);

    // Set gradient and glow toggles as data attributes for CSS selectors
    root.setAttribute('data-gradient-enabled', theme.gradientEnabled ? 'true' : 'false');
    root.setAttribute('data-glow-enabled', theme.buttonGlowEnabled ? 'true' : 'false');

    // Set visual style data attribute for CSS class switching
    root.setAttribute('data-visual-style', theme.visualStyle || 'glassmorphic');

    // Apply custom CSS if provided
    if (theme.customCSS && theme.customCSS.trim().length > 0) {
      this.injectCustomCSS(theme.customCSS);
    } else {
      this.removeCustomCSS();
    }

    // Store in localStorage for faster initial load
    try {
      localStorage.setItem('levelup-theme', JSON.stringify(theme));
    } catch (error) {
      console.error('Error storing theme in localStorage:', error);
    }
  }

  /**
   * Inject custom CSS into the document
   */
  private injectCustomCSS(css: string): void {
    if (!this.customStyleElement) {
      this.customStyleElement = document.createElement('style');
      this.customStyleElement.id = 'custom-theme-css';
      document.head.appendChild(this.customStyleElement);
    }

    this.customStyleElement.textContent = css;
  }

  /**
   * Remove custom CSS from the document
   */
  private removeCustomCSS(): void {
    if (this.customStyleElement) {
      this.customStyleElement.remove();
      this.customStyleElement = null;
    }
  }

  /**
   * Update theme and save to API
   */
  async updateTheme(updates: Partial<ThemeConfig>): Promise<void> {
    try {
      // Prepare settings updates
      const settingsToUpdate: Record<string, any> = {};

      if (updates.primaryColor !== undefined) {
        settingsToUpdate['theme.primary_color'] = { color: updates.primaryColor };
      }

      if (updates.secondaryColor !== undefined) {
        settingsToUpdate['theme.secondary_color'] = { color: updates.secondaryColor };
      }

      if (updates.accentColor !== undefined) {
        settingsToUpdate['theme.accent_color'] = { color: updates.accentColor };
      }

      if (updates.backgroundColor !== undefined) {
        settingsToUpdate['theme.background_color'] = { color: updates.backgroundColor };
      }

      if (updates.surfaceColor !== undefined) {
        settingsToUpdate['theme.surface_color'] = { color: updates.surfaceColor };
      }

      if (updates.textColor !== undefined) {
        settingsToUpdate['theme.text_color'] = { color: updates.textColor };
      }

      if (updates.fontFamily !== undefined) {
        settingsToUpdate['theme.font_family'] = { font: updates.fontFamily };
      }

      if (updates.customCSS !== undefined) {
        settingsToUpdate['theme.custom_css'] = { css: updates.customCSS };
      }

      if (updates.visualStyle !== undefined) {
        settingsToUpdate['theme.visual_style'] = { style: updates.visualStyle };
      }

      if (updates.gradientEnabled !== undefined) {
        settingsToUpdate['theme.gradient_enabled'] = { enabled: updates.gradientEnabled };
      }

      if (updates.gradientStartColor !== undefined) {
        settingsToUpdate['theme.gradient_start'] = { color: updates.gradientStartColor };
      }

      if (updates.gradientMiddleColor !== undefined) {
        settingsToUpdate['theme.gradient_middle'] = { color: updates.gradientMiddleColor };
      }

      if (updates.gradientEndColor !== undefined) {
        settingsToUpdate['theme.gradient_end'] = { color: updates.gradientEndColor };
      }

      if (updates.buttonGlowEnabled !== undefined) {
        settingsToUpdate['theme.button_glow_enabled'] = { enabled: updates.buttonGlowEnabled };
      }

      // Update all settings at once via API
      await apiPut('/api/admin/settings', { settings: settingsToUpdate });

      // Reload theme from API
      await this.loadTheme();
    } catch (error) {
      console.error('Error updating theme:', error);
      throw error;
    }
  }

  /**
   * Load cached theme from localStorage immediately (before async load)
   */
  loadCachedTheme(): ThemeConfig {
    try {
      const cached = localStorage.getItem('levelup-theme');
      if (cached) {
        const theme = JSON.parse(cached) as ThemeConfig;
        this.applyTheme(theme);
        this.currentTheme = theme;
        return theme;
      }
    } catch (error) {
      console.error('Error loading cached theme:', error);
    }

    this.applyTheme(DEFAULT_THEME);
    return DEFAULT_THEME;
  }

  /**
   * Get current theme
   */
  getCurrentTheme(): ThemeConfig {
    return { ...this.currentTheme };
  }

  /**
   * Reset theme to default
   */
  async resetTheme(): Promise<void> {
    await this.updateTheme(DEFAULT_THEME);
  }
}

// React hook for theme management
export function useTheme() {
  const [theme, setTheme] = useState<ThemeConfig>(DEFAULT_THEME);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const manager = ThemeManager.getInstance();

    // Load cached theme immediately for instant UX
    const cachedTheme = manager.loadCachedTheme();
    setTheme(cachedTheme);

    // Then load fresh theme from API
    manager
      .loadTheme()
      .then((loadedTheme) => {
        setTheme(loadedTheme);
      })
      .catch((err) => {
        console.error('Error loading theme:', err);
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const updateTheme = async (updates: Partial<ThemeConfig>) => {
    try {
      const manager = ThemeManager.getInstance();
      await manager.updateTheme(updates);
      setTheme(manager.getCurrentTheme());
    } catch (err) {
      console.error('Error updating theme:', err);
      setError(err instanceof Error ? err : new Error('Failed to update theme'));
      throw err;
    }
  };

  const resetTheme = async () => {
    try {
      const manager = ThemeManager.getInstance();
      await manager.resetTheme();
      setTheme(manager.getCurrentTheme());
    } catch (err) {
      console.error('Error resetting theme:', err);
      setError(err instanceof Error ? err : new Error('Failed to reset theme'));
      throw err;
    }
  };

  return {
    theme,
    loading,
    error,
    updateTheme,
    resetTheme,
  };
}
